import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, Copy, Check, AlertTriangle, ArrowRight, Code, Send, Loader2, ExternalLink, Plug } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

const SEV_COLORS = {
  critical: "#EF4444",
  warning: "#F59E0B",
  info: "#0055FF",
};

const APPLY_CAPABLE_KINDS = ["wordpress", "shopify", "ghost", "webhook", "api"];

export default function FixKitPage() {
  const { active } = useProjects();
  const [kit, setKit] = useState(null);
  const [error, setError] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  useEffect(() => {
    if (!active) return;
    api.get(`/projects/${active.id}/fix-kit`)
      .then(({ data }) => setKit(data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [active?.id]);

  useEffect(() => {
    api.get("/integrations").then(({ data }) => setIntegrations(data)).catch(() => {});
  }, []);

  const applyCapableIntegs = integrations.filter(i => i.enabled && APPLY_CAPABLE_KINDS.includes(i.kind));

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1200);
  };

  if (!active) {
    return (
      <DashboardLayout eyebrow="Auto-Fix Kit" title="Paste-able SEO patches." subtitle="Generated from your last audit. Copy → paste → ship.">
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center">
          <Wrench className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <p className="text-zinc-400">Activate a project to view its fix kit.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!kit) {
    return (
      <DashboardLayout eyebrow="Auto-Fix Kit" title="Paste-able SEO patches." subtitle="Generated from your last audit. Copy → paste → ship.">
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="fixkit-empty">
          <Wrench className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No fix kit yet</h3>
          <p className="text-zinc-400 mb-6">{error || "Run autopilot on this project to generate one."}</p>
          <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
            <Link to="/autopilot">Run autopilot <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const r = kit.result || {};

  return (
    <DashboardLayout
      eyebrow="Auto-Fix Kit"
      title={`Fix kit for ${active.name}`}
      subtitle="Auto-apply patches to your connected CMS — or copy each one to paste manually."
      action={
        <Button
          onClick={() => { setApplyResult(null); setApplyOpen(true); }}
          disabled={applyCapableIntegs.length === 0}
          className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-10"
          data-testid="fixkit-apply-cta"
        >
          <Send className="w-4 h-4 mr-2" />
          {applyCapableIntegs.length === 0 ? "Connect a CMS to apply" : "Apply to site"}
        </Button>
      }
    >
      {applyCapableIntegs.length === 0 ? (
        <div className="bg-amber-950/20 border border-amber-900/40 rounded-sm p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="fixkit-disclaimer">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-200/80 leading-relaxed flex-1 min-w-[260px]">
            <strong className="text-amber-100">Want one-click apply?</strong> Connect WordPress, Shopify, Ghost, or a Custom Webhook and we'll write these patches straight to your CMS as a draft.
          </p>
          <Button asChild variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="fixkit-connect-cta">
            <Link to="/integrations"><Plug className="w-3.5 h-3.5 mr-1.5" /> Connect a CMS</Link>
          </Button>
        </div>
      ) : (
        <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="fixkit-ready">
          <Check className="w-4 h-4 text-[#10B981] flex-shrink-0" />
          <p className="text-sm text-zinc-300 leading-relaxed flex-1">
            Ready to auto-apply to <strong className="text-[#10B981]">{applyCapableIntegs.length}</strong> connected {applyCapableIntegs.length === 1 ? "site" : "sites"}. Patches arrive as a <em>draft</em> — review and publish from your CMS.
          </p>
        </div>
      )}

      <Tabs defaultValue="patches">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="fixkit-tabs">
          <TabsTrigger value="patches" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-patches">Patches ({(r.patches || []).length})</TabsTrigger>
          <TabsTrigger value="meta" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-meta">Meta tags</TabsTrigger>
          <TabsTrigger value="schema" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-schema">JSON-LD Schema</TabsTrigger>
          <TabsTrigger value="robots" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-robots">robots.txt</TabsTrigger>
        </TabsList>

        <TabsContent value="patches" className="mt-4 space-y-3">
          {(r.patches || []).map((p, i) => (
            <div key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid={`patch-${i}`}>
              <div className="px-6 py-4 border-b border-zinc-800 flex items-start gap-3">
                <Badge className="rounded-sm" style={{ background: (SEV_COLORS[p.severity] || "#71717A") + "15", color: SEV_COLORS[p.severity] || "#A1A1AA", borderColor: (SEV_COLORS[p.severity] || "#71717A") + "40" }}>{p.severity}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-100">{p.issue_title}</div>
                  <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{p.what_to_do}</p>
                  {p.where && <div className="text-xs text-[#3377FF] mt-2 font-mono">→ {p.where}</div>}
                </div>
              </div>
              {p.code && (
                <div className="relative">
                  <Button onClick={() => copy(p.code, `patch-${i}`)} variant="outline" size="sm"
                          className="absolute top-3 right-3 bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm z-10"
                          data-testid={`patch-copy-${i}`}>
                    {copiedId === `patch-${i}` ? <><Check className="w-3 h-3 mr-1.5 text-[#10B981]" /> Copied</> : <><Copy className="w-3 h-3 mr-1.5" /> Copy</>}
                  </Button>
                  <pre className="text-xs text-zinc-300 p-6 overflow-x-auto bg-[#09090B] font-mono whitespace-pre-wrap break-words"><code>{p.code}</code></pre>
                </div>
              )}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="meta" className="mt-4">
          <CodeBlock label="Paste in <head>" code={r.meta_tags || ""} onCopy={(c) => copy(c, "meta")} copied={copiedId === "meta"} />
        </TabsContent>

        <TabsContent value="schema" className="mt-4">
          <CodeBlock label="Paste in <head> on homepage" code={typeof r.json_ld_schema === "string" ? r.json_ld_schema : JSON.stringify(r.json_ld_schema, null, 2)} onCopy={(c) => copy(c, "schema")} copied={copiedId === "schema"} />
        </TabsContent>

        <TabsContent value="robots" className="mt-4">
          <CodeBlock label="Save as /robots.txt" code={r.robots_txt || ""} onCopy={(c) => copy(c, "robots")} copied={copiedId === "robots"} />
          {r.sitemap_hint && (
            <div className="mt-4 bg-zinc-950 border border-zinc-800 rounded-sm p-5" data-testid="sitemap-hint">
              <span className="label-eyebrow">Sitemap</span>
              <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{r.sitemap_hint}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ApplyDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        kitId={kit?.id}
        integrations={applyCapableIntegs}
        onResult={setApplyResult}
        result={applyResult}
      />
    </DashboardLayout>
  );
}

function ApplyDialog({ open, onClose, kitId, integrations, onResult, result }) {
  const [selectedId, setSelectedId] = useState(integrations[0]?.id || "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && !selectedId && integrations.length) setSelectedId(integrations[0].id);
  }, [open, integrations, selectedId]);

  const apply = async () => {
    if (!selectedId || !kitId) return;
    setSubmitting(true);
    onResult(null);
    try {
      const { data } = await api.post("/fix-kit/apply", { kit_id: kitId, integration_id: selectedId });
      onResult(data);
      if (data.ok) toast.success(`Applied to ${data.integration_name}`);
      else toast.error(data.error || "Apply failed");
    } catch (e) {
      onResult({ ok: false, error: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-md" data-testid="fixkit-apply-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Send className="w-5 h-5 text-[#0055FF]" /> Auto-apply fix kit
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Choose where to send the patches. We'll create a draft page/post in your CMS — review then publish.
          </DialogDescription>
        </DialogHeader>

        {integrations.length === 0 ? (
          <div className="text-sm text-zinc-400">No connected sites that support auto-apply.</div>
        ) : (
          <div className="space-y-2" data-testid="fixkit-apply-list">
            {integrations.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => setSelectedId(i.id)}
                className={`w-full text-left flex items-center gap-3 p-3 border rounded-sm transition-colors ${selectedId === i.id ? "border-[#0055FF] bg-[#0055FF]/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}
                data-testid={`fixkit-apply-target-${i.id}`}
              >
                <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${selectedId === i.id ? "border-[#0055FF] bg-[#0055FF]" : "border-zinc-700"}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{i.name}</div>
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500">{i.kind}</div>
                </div>
              </button>
            ))}
          </div>
        )}

        {result && (
          <div className={`text-sm px-3 py-2.5 rounded-sm border ${result.ok ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-red-900/40 bg-red-950/30 text-red-400"}`} data-testid="fixkit-apply-result">
            {result.ok ? (
              <>
                <div className="font-medium mb-1">✓ Applied {result.summary?.total || 0} patches via {result.method}</div>
                {result.edit_link && <a href={result.edit_link} target="_blank" rel="noreferrer" className="text-[#3377FF] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open in CMS</a>}
              </>
            ) : (
              <>Apply failed: {result.error || "unknown error"}</>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm">Close</Button>
          <Button onClick={apply} disabled={submitting || !selectedId} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="fixkit-apply-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Apply now</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CodeBlock({ label, code, onCopy, copied }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="codeblock">
      <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-[#3377FF]" />
          <span className="text-sm text-zinc-300">{label}</span>
        </div>
        <Button onClick={() => onCopy(code)} variant="outline" size="sm" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm">
          {copied ? <><Check className="w-3 h-3 mr-1.5 text-[#10B981]" /> Copied</> : <><Copy className="w-3 h-3 mr-1.5" /> Copy</>}
        </Button>
      </div>
      <pre className="text-xs text-zinc-300 p-6 overflow-x-auto bg-[#09090B] font-mono whitespace-pre-wrap break-words"><code>{code}</code></pre>
    </div>
  );
}
